import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { Search, Loader2 } from "lucide-react";
import { Input } from "../components/ui/input";
import axios from "axios";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const ARCHETYPE_ICONS = {
  critico_interior: "⚖️",
  nino_herido: "💔",
  protector: "🛡️",
  complacedor: "🤝",
  guerrero: "⚔️",
  sabio: "📜",
  nino_libre: "✨",
  cuidador: "💚",
  perfeccionista: "🎯",
  rebelde: "🔥",
  amante: "❤️",
  ermitano: "🏔️",
  estratega: "🧠",
  sanador: "🌿",
  self: "☀️"
};

const ARCHETYPE_COLORS = {
  critico_interior: "#C62828",
  nino_herido: "#5C6BC0",
  protector: "#37474F",
  complacedor: "#F06292",
  guerrero: "#D32F2F",
  sabio: "#7E57C2",
  nino_libre: "#FFD54F",
  cuidador: "#81C784",
  perfeccionista: "#00897B",
  rebelde: "#F57C00",
  amante: "#E91E63",
  ermitano: "#78909C",
  estratega: "#5E35B1",
  sanador: "#26A69A",
  self: "#FFC107"
};

export default function BibliotecaPage() {
  const [arquetipos, setArquetipos] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState("");

  useEffect(() => {
    const fetchArquetipos = async () => {
      try {
        const response = await axios.get(`${API}/arquetipos`);
        setArquetipos(response.data);
      } catch (error) {
        console.error("Error fetching arquetipos:", error);
      } finally {
        setLoading(false);
      }
    };
    fetchArquetipos();
  }, []);

  const filteredArquetipos = arquetipos.filter(arq => 
    arq.nombre.toLowerCase().includes(searchTerm.toLowerCase()) ||
    arq.nombreIngles.toLowerCase().includes(searchTerm.toLowerCase()) ||
    arq.descripcion.toLowerCase().includes(searchTerm.toLowerCase())
  );

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <Loader2 className="animate-spin text-primary" size={48} />
      </div>
    );
  }

  return (
    <div className="min-h-screen py-12 px-4 sm:px-6 lg:px-8">
      <div className="max-w-6xl mx-auto">
        {/* Header */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="text-center mb-12"
        >
          <h1 className="font-heading text-4xl sm:text-5xl text-white mb-4">
            Biblioteca de{" "}
            <span className="text-secondary italic">Arquetipos</span>
          </h1>
          <p className="font-body text-lg text-primary-200 max-w-2xl mx-auto mb-8">
            Explora los 15 arquetipos universales. Cada uno tiene dones únicos que ofrecer 
            y sombras que integrar.
          </p>

          {/* Search */}
          <div className="relative max-w-md mx-auto">
            <Search className="absolute left-4 top-1/2 -translate-y-1/2 text-primary-400" size={20} />
            <Input
              type="text"
              placeholder="Buscar arquetipo..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="pl-12 bg-primary-950/50 border-white/10 rounded-full"
              data-testid="search-input"
            />
          </div>
        </motion.div>

        {/* Grid */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
          {filteredArquetipos.map((arq, index) => (
            <motion.div
              key={arq.id}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: index * 0.05 }}
            >
              <Link
                to={`/arquetipo/${arq.id}`}
                className="block glass-card glass-card-hover rounded-xl p-6 h-full group"
                style={{ borderLeft: `4px solid ${ARCHETYPE_COLORS[arq.id]}` }}
                data-testid={`biblioteca-card-${arq.id}`}
              >
                <div className="flex items-start gap-4">
                  <span className="text-4xl">{ARCHETYPE_ICONS[arq.id]}</span>
                  <div className="flex-1 min-w-0">
                    <h3 className="font-heading text-xl text-white group-hover:text-secondary transition-colors">
                      {arq.nombre}
                    </h3>
                    <p className="font-body text-sm text-primary-400 mb-3">
                      {arq.nombreIngles}
                    </p>
                    <p className="font-body text-sm text-primary-200 line-clamp-3">
                      {arq.descripcion}
                    </p>
                  </div>
                </div>

                {/* Tags */}
                <div className="mt-4 flex flex-wrap gap-2">
                  {arq.dones?.slice(0, 2).map((don, i) => (
                    <span 
                      key={i}
                      className="text-xs font-body px-2 py-1 rounded-full bg-secondary/10 text-secondary"
                    >
                      {don.split(' ').slice(0, 2).join(' ')}
                    </span>
                  ))}
                </div>
              </Link>
            </motion.div>
          ))}
        </div>

        {filteredArquetipos.length === 0 && (
          <div className="text-center py-12">
            <p className="font-body text-primary-300">
              No se encontraron arquetipos con ese término.
            </p>
          </div>
        )}
      </div>
    </div>
  );
}
